import { ArrowRight, ChevronDown, Download } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import type { ScoutDownload } from '@/features/discovery/helpers/scout-download';
import { useScoutDownload } from '@/features/discovery/hooks';
import { NewSynclyLogo } from '@/shared/components/new-syncly-logo';
import { Button, buttonVariants } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';

const PLATFORM_LABEL = {
  mac: 'macOS',
  win: 'Windows',
} as const;

const MAC_ARCHITECTURE_LABEL = {
  arm64: 'Apple Silicon',
  x64: 'Intel',
} as const;

function ScoutDownloadLink({ download }: { download: ScoutDownload }) {
  const architectureLabel =
    download.platform === 'mac' ? MAC_ARCHITECTURE_LABEL[download.architecture] : null;

  return (
    <a
      href={download.url}
      className={buttonVariants({
        className: 'min-h-12 w-full max-w-60 px-4 py-3 text-left sm:w-auto sm:max-w-none',
      })}
    >
      <span className="text-lg leading-6 font-normal">
        Download for {PLATFORM_LABEL[download.platform]}
        <br />
        <span className="text-sm">
          {architectureLabel ? `${architectureLabel} · ` : ''}v{download.version}
        </span>
      </span>
      <Download aria-hidden="true" />
    </a>
  );
}

function MacDownloadDropdown({ downloads }: { downloads: ScoutDownload[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          className="group min-h-12 w-fit justify-center px-0 py-3 text-lg font-normal"
        >
          <div className="flex items-center">
            <span className="px-4">Download for macOS</span>
            <div className="h-6 w-px bg-zinc-700"></div>
            <div className="px-4">
              <ChevronDown
                aria-hidden="true"
                data-icon="inline-end"
                className="transition-transform group-data-[state=open]:rotate-180"
              />
            </div>
          </div>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="bg-primary text-primary-foreground w-52 min-w-40 p-1"
      >
        <DropdownMenuGroup>
          {downloads.map((download, index) => (
            <Fragment key={`${download.platform}-${download.architecture}`}>
              {index > 0 && <DropdownMenuSeparator className="bg-zinc-700" />}
              <a href={download.url} className="flex items-center px-3 py-1 text-sm">
                <span className="flex-1 text-left">
                  {MAC_ARCHITECTURE_LABEL[download.architecture]} · v{download.version}
                </span>
                <Download aria-hidden="true" className="text-primary-foreground size-5" />
              </a>
            </Fragment>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const ScoutDownloadPage = () => {
  const downloadState = useScoutDownload();
  let appDownloadButton: ReactNode;

  if (downloadState.status === 'ready') {
    const macDownloads = downloadState.downloads.filter((download) => download.platform === 'mac');

    appDownloadButton =
      macDownloads.length > 1 ? (
        <MacDownloadDropdown downloads={macDownloads} />
      ) : (
        downloadState.downloads.map((download) => (
          <ScoutDownloadLink
            key={`${download.platform}-${download.architecture}`}
            download={download}
          />
        ))
      );
  } else {
    const platformLabel =
      downloadState.status === 'unsupported'
        ? 'macOS or Windows'
        : PLATFORM_LABEL[downloadState.platform];

    appDownloadButton = (
      <Button
        type="button"
        disabled
        className="min-h-12 w-full max-w-60 px-4 py-3 text-lg font-normal whitespace-normal sm:w-auto sm:max-w-none sm:px-5"
        loading={downloadState.status === 'loading'}
      >
        Download for {platformLabel}
      </Button>
    );
  }

  return (
    <main className="bg-background flex min-h-screen w-full items-center justify-center px-6 py-16">
      <section className="flex w-full max-w-3xl flex-col items-center text-center">
        <NewSynclyLogo className="size-24" />

        <div className="mt-10 space-y-3">
          <h1 className="text-foreground text-3xl leading-tight font-bold">
            Download Creator Scout
          </h1>
          <p className="text-foreground text-base leading-6">
            To use Creator Scout, you need both the App and the Chrome extension.
          </p>
        </div>

        <div className="mt-12 flex w-full flex-col flex-wrap items-center justify-center gap-4 sm:w-auto sm:flex-row sm:gap-6">
          {appDownloadButton}
          <Button
            type="button"
            variant="outline"
            className="border-primary min-h-12 w-full max-w-60 px-4 py-3 text-lg font-normal whitespace-normal sm:w-auto sm:max-w-none sm:px-5"
            onClick={() =>
              window.open(
                'https://chromewebstore.google.com/detail/creator-scout/lhlmpoolnhahpdhbkcpojncoanokllbg',
              )
            }
          >
            Get Chrome Extension
            <ArrowRight className="size-5" />
          </Button>
        </div>

        {downloadState.status === 'error' && (
          <p className="text-muted-foreground mt-4 text-sm" role="status">
            Download information is temporarily unavailable. Please refresh and try again.
          </p>
        )}
        {downloadState.status === 'unsupported' && (
          <p className="text-muted-foreground mt-4 text-sm" role="status">
            Creator Scout is currently available for macOS and Windows desktop devices.
          </p>
        )}
      </section>
    </main>
  );
};
